import { FC, useState } from "react";
import { Box, Container, Grid, Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";
import { SSButton } from "src/components/base/components/SSButton";
import { LazyImage } from "src/components/base/components/LazyImage";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSAuth } from "src/components/reusable/hooks/useSSAuth";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useSeatGeek } from "src/components/reusable/hooks/useSeatGeek";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useTicketmaster } from "src/components/reusable/hooks/useTicketmaster";
import { useTicketsdotcom } from "src/components/reusable/hooks/useTicketsdotcom";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

export const StaticBannerRegistrationCard: FC = () => {
  const theme = useSSTheme();
  const router = useRouterExt();
  const { md: mdUp } = useCssMediaQuery("up");
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useSSAuth();
  const { connectSeatGeek } = useSeatGeek();
  const { connectTicketmaster } = useTicketmaster();
  const { connectTicketsdotcom } = useTicketsdotcom();

  const { LANDING_PAGE_SETTINGS, IMAGES, TEAM_FULL_NAME, VENDOR } =
    useTeamConfig();

  async function handleClickCTA(authRedirectPage: string) {
    setIsLoading(true);

    if (!user) {
      const state = {
        authOrigin: constants.authOrigins.landingRegistrationCard,
        redirectRoute: { pathname: routes.home },
      };

      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return await connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return await connectTicketsdotcom(state);
      }

      return await connectTicketmaster({ state });
    }

    await router.pushSchema(authRedirectPage);
    setIsLoading(false);
  }

  return (
    <Box position={"relative"}>
      <Box
        width={"100%"}
        height={"450px"}
        sx={{
          filter: "grayscale(1) brightness(0.3)",
        }}
      >
        <LazyImage
          width={2160}
          height={2700}
          alt={`${TEAM_FULL_NAME} Image`}
          src={IMAGES.LANDING_PAGE_STATIC_BANNER_URL as string}
          style={{ objectFit: "cover", width: "100%", height: "100%" }}
        />
      </Box>

      <Box width={"100%"} position="absolute" top={100}>
        <Container maxWidth={"lg"}>
          <Stack spacing={2}>
            <Box>
              <Grid
                container
                spacing={3}
                justifyContent={mdUp ? "center" : "flex-start"}
              >
                {LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS?.REGISTRATION_CARDS?.map(
                  (itm, idx) => {
                    return (
                      <Grid item key={idx} xs={12} sm={6} lg={4}>
                        <Stack
                          direction={"column"}
                          borderRadius={2.5}
                          alignItems={"flex-start"}
                          overflow={"hidden"}
                          height={"100%"}
                        >
                          <Stack p={2} spacing={2} flex={1} width={"100%"}>
                            <Stack spacing={2}>
                              <SSText
                                variant="h2"
                                color={theme.colors.teamPrimary}
                                lineHeight={1.1}
                                fontWeight={500}
                                textAlign={"center"}
                              >
                                {itm.title}
                              </SSText>
                              <SSText
                                variant="body"
                                color={theme.colors.gray[100]}
                                textAlign={"center"}
                              >
                                {itm.description}
                              </SSText>
                            </Stack>

                            <Box flexGrow={1} />

                            <SSButton
                              fullWidth
                              variant="contained"
                              loading={isLoading}
                              disabled={isLoading}
                              onClick={() =>
                                handleClickCTA(itm.authRedirectPage)
                              }
                            >
                              {itm?.buttonLabel || "View Offers"}
                            </SSButton>
                          </Stack>
                        </Stack>
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
